"use client";

import CopyButton from "@/components/ui/copy-button";
import { Switch } from "@/components/ui/switch";
import { useEffect, useRef, useState } from "react";

export function WebappBentogrid() {
  const [showCharts, setShowCharts] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!userInteracted) {
      intervalRef.current = setInterval(() => {
        setShowCharts((prev) => !prev);
      }, 3000); // ogni 3 secondi

      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
      };
    }
  }, [userInteracted]);

  const handleUserToggle = () => {
    if (!userInteracted && intervalRef.current) {
      clearInterval(intervalRef.current); // Ferma l'alternanza automatica
    }
    setUserInteracted(true);
    setShowCharts((prev) => !prev);
  };

  return (
    <div className="h-full w-full p-4 overflow-hidden">
      <h3 className="text-md sm:text-lg lg:text-2xl font-semibold">
        Complex apps, tailored.
      </h3>

      {/* Page  */}
      <div className="relative group group-hover:opacity-30 bg-white dark:bg-black rounded-xl w-full h-[290px] md:h-[330px] lg:h-[340px] max-w-md mx-auto mt-3 md:mt-4 border border-[#C5C5C5] dark:border-[#333333] space-y-4">
        {/* Floating Button */}
        <div className="absolute z-50 top-3/4 sm:top-4/5 md:top-5/6 lg:top-4/5 right-4/9 sm:right-1/2 md:right-5/9 lg:right-1/2 bg-white dark:bg-[#272727] border border-[#C5C5C5] dark:border-[#6E6E6E] px-5 py-3 rounded-md shadow-lg transition-all">
          <div className="flex flex-row gap-1">
            <h4 className="text-[#A1A1A1]"></h4>Charts &nbsp;
            <Switch
              checked={showCharts}
              onClick={handleUserToggle}
              className="cursor-pointer mt-1"
            />
          </div>
        </div>

        {/* Address bar */}
        <div className="flex items-center rounded-xl rounded-b-none bg-[#F0F0F0] dark:bg-[#0A0A0A] p-2 pl-4 gap-8 py-2">
          <div className="flex flex-row gap-2 transition-all md:group-hover:blur-xs">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <div className="w-3 h-3 rounded-full bg-green-500" />
          </div>
          <div className="relative flex w-full items-center pl-2 truncate justify-between rounded-full py-0.5 transition-all border md:border-[#C5C5C5] md:dark:border-[#1E1E1E] md:group-hover:border-[#CCE7FF] border-[#CCE7FF] md:group-hover:dark:border-[#0D3868] dark:border-[#0D3868] md:group-hover:bg-[#EFF7FF] md:group-hover:dark:bg-[#0F1C2E] md:bg-white bg-[#EFF7FF] md:dark:bg-black dark:bg-[#0F1C2E]">
            <p className="flex-1 min-w-0 text-sm truncate text-center">
              myapp.com
            </p>
            <div className="flex-shrink-0 w-9 flex items-center justify-center">
              <CopyButton text="myapp.com" />
            </div>
          </div>
        </div>

        {/* Navbar */}
        <div className="w-full flex flex-row justify-between px-4 transition-all md:group-hover:blur-xs">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded-full bg-[#C9C9C9] dark:bg-[#5C5C5C]" />
            <div className="w-16 h-3 rounded bg-[#C9C9C9] dark:bg-[#5C5C5C]" />
          </div>
          <div className="flex space-x-2">
            <div className="w-4 h-3 rounded bg-[#C9C9C9] dark:bg-[#5C5C5C]" />
            <div className="w-4 h-3 rounded bg-[#C9C9C9] dark:bg-[#5C5C5C]" />
            <div className="w-4 h-3 rounded bg-[#C9C9C9] dark:bg-[#5C5C5C]" />
          </div>
        </div>

        {/* Charts: Appear above with no movement */}
        <div
          className={`z-20 relative mb-4 transition-all duration-500 ease-in-out transform ${
            showCharts ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <div className="grid grid-cols-2 place-items-center">
            {/* Pie Chart */}
            <div className="p-2">
              <div className="w-24 h-24 relative rounded-full bg-[#EFF7FF] dark:bg-[#0F1C2E] border border-[#CCE7FF] dark:border-[#0D3868]">
                <div className="absolute z-0 w-[57px] h-2 left-[37%] top-[67.4%] -translate-x-1/2 -translate-y-1/2 rotate-[-45deg] bg-white dark:bg-[#272727] border-t border-b border-[#CCE7FF] dark:border-[#0D3868]" />
                <div className="absolute z-10 w-[55px] h-2 left-[40%] top-[29%] -translate-x-1/2 -translate-y-1/2 rotate-[45deg] bg-white dark:bg-[#272727] border-t border-b border-r border-[#CCE7FF] dark:border-[#0D3868]" />
                {/* Mask to remove the border */}
                <div className="absolute z-20 w-[6px] h-3 left-[53%] top-[51%] -translate-x-1/2 -translate-y-1/2 rotate-[45deg] bg-white dark:bg-[#272727]"></div>
              </div>
            </div>

            {/* Bar Chart */}
            <div className="flex flex-col items-start gap-3 w-28 h-28 p-3 rounded-xl bg-[#EFF7FF] dark:bg-[#0F1C2E] border border-[#CCE7FF] dark:border-[#0D3868]">
              <div className="w-4/6 bg-[#C9C9C9] dark:bg-[#5C5C5C] rounded-r h-3" />
              <div className="w-3/6 bg-[#C9C9C9] dark:bg-[#5C5C5C] rounded-r h-3" />
              <div className="w-2/6 bg-[#C9C9C9] dark:bg-[#5C5C5C] rounded-r h-3" />
              <div className="w-5/6 bg-[#C9C9C9] dark:bg-[#5C5C5C] rounded-r h-3" />
            </div>
          </div>
        </div>

        {/* Content Rectangles: Slide down */}
        <div
          className={`relative z-30 transition-transform duration-800 ease-in-out px-4 md:group-hover:blur-xs ${
            showCharts ? "translate-y-0" : "-translate-y-30"
          }`}
        >
          <div className="grid grid-cols-2 gap-4 mb-2">
            <div className="h-16 rounded-md bg-[#C9C9C9] dark:bg-[#5C5C5C] shadow-md" />
            <div className="h-16 rounded-md bg-[#C9C9C9] dark:bg-[#5C5C5C] shadow-md" />
          </div>
          <div className="flex flex-col gap-2">
            <div className="h-24 rounded-md bg-[#C9C9C9] dark:bg-[#5C5C5C] shadow-md" />
            <div className="h-24 rounded-md bg-[#C9C9C9] dark:bg-[#5C5C5C] shadow-md" />
          </div>
        </div>
        {/* Shadow */}
        <div className="absolute -bottom-5 md:-bottom-10 lg:-bottom-5 -left-2 w-[110%] h-14 md:h-20 lg:h-30 bg-gradient-to-t from-[#FAFAFA] via-[#FAFAFA] to-transparent dark:from-black dark:via-black pointer-events-none z-[40]" />
      </div>
    </div>
  );
}
